import {
  type AccountChainData,
  type ChainFullId,
  ChainIds,
  type ImplicitAccount,
  type LedgerAccount,
  type Network,
  getChainFullIdFromNetwork,
  parseChainFullId,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  useAsyncActionHandler,
  useAvailableMultichainNetworks,
  useCurrentAccount,
  useDisableChain,
  useEnableChain,
  useGetMultichainSecretKey,
  useIsChainEnabled,
  useSelectNetwork,
  useSelectedNetwork,
} from "@umami/state";

import { MasterPasswordModal } from "@/components/MasterPasswordModal";

// For now Ledger account can have only one chain
type NonLedgerAccount = Exclude<ImplicitAccount, LedgerAccount>;

export const useToggleNetwork = () => {
  const currentAccount = useCurrentAccount() as NonLedgerAccount;
  const enableChain = useEnableChain();
  const disableChain = useDisableChain();
  const currentNetwork = useSelectedNetwork();
  const selectNetwork = useSelectNetwork();
  const isChainEnabled = useIsChainEnabled();
  const getSecretKey = useGetMultichainSecretKey();
  const { networks } = useAvailableMultichainNetworks();

  const { openWith, onClose } = useDynamicModalContext();
  const { handleAsyncAction } = useAsyncActionHandler();

  const getChainData = (network: Network) => {
    const chain = Object.keys(currentAccount.chains ?? {}).find(key => {
      const { namespace } = parseChainFullId(key as ChainFullId);

      return namespace === network.namespace;
    }) as ChainFullId | undefined;

    return chain && currentAccount.chains?.[chain];
  };

  const enableChainWithPassword = async (chainId: ChainFullId) => {
    const handleSubmit = async (password: string = "") =>
      handleAsyncAction(async () => {
        const credentials = await getSecretKey(currentAccount, password);

        if (currentAccount.type === "mnemonic" && credentials.mnemonic) {
          await enableChain(chainId, {
            mnemonic: credentials.mnemonic,
            derivationPaths: currentAccount.derivationPaths ?? {},
          });
        } else if (credentials.secretKey) {
          await enableChain(chainId, { secretKey: credentials.secretKey });
        }

        onClose();
      });

    return openWith(
      <MasterPasswordModal defaultAccount={currentAccount} onSubmit={handleSubmit} />
    );
  };

  const enableChainWithExistingData = async (chainId: ChainFullId, chainData: AccountChainData) =>
    await enableChain(chainId, {
      address: chainData.address.pkh,
      pk: chainData.pk,
      derivationPath: chainData.derivationPath,
    });

  const handleMultichainToggle = async (network: Network) => {
    const chainId = getChainFullIdFromNetwork(network);

    if (isChainEnabled(network)) {
      return disableChain(chainId);
    }

    const chainData = getChainData(network);

    if (chainData) {
      // Use existing chain data from the same namespace
      await enableChainWithExistingData(chainId, chainData);
    } else {
      // Need to derive new keys - requires password
      await enableChainWithPassword(chainId);
    }
  };

  const handleTezosToggle = (network: Network) => {
    if (currentNetwork.name === network.name) {
      // Switch to the other Tezos network
      const otherTezosNetwork = networks.find(
        n => n.chain === ChainIds.TEZOS && n.name !== network.name
      );

      if (otherTezosNetwork) {
        selectNetwork(otherTezosNetwork.name);
      }
    } else {
      // Switch to the selected Tezos network
      selectNetwork(network.name);
    }
  };

  return async (network: Network) =>
    handleAsyncAction(async () => {
      if (network.chain === ChainIds.TEZOS) {
        handleTezosToggle(network);
      } else {
        await handleMultichainToggle(network);
      }
    });
};
